import { catalogForChapter } from "@repo/vihangam-yog-shared";
import { useMemo, useState } from "react";

import {
  eventsForMonth,
  monthGrid,
  monthLabel,
  nextMonth,
  prevMonth,
  type YearMonth,
} from "../content/calendar";
import { t } from "../i18n/dict";
import { langFromPath, localePath } from "../i18n/lang";

const WEEKDAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function todayYearMonth(): YearMonth {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/**
 * A Monday-first month grid reading the Singapore catalog's recurring
 * sessions and dated events straight from `../content/calendar`'s pure
 * helpers - no comment box, no reCAPTCHA (the old calendar's builder
 * cruft, dropped rather than rebuilt). `initial` seeds the shown month;
 * omit it to start on the real current month.
 */
export function CalendarMonth({ initial }: { initial?: YearMonth }) {
  const [shown, setShown] = useState<YearMonth>(initial ?? todayYearMonth());
  const { lang } = langFromPath(window.location.pathname);
  const catalog = useMemo(() => catalogForChapter("sg"), []);
  const weeks = useMemo(
    () => monthGrid(shown.year, shown.month),
    [shown.year, shown.month],
  );
  const eventsByDay = useMemo(
    () => eventsForMonth(shown.year, shown.month, catalog),
    [shown.year, shown.month, catalog],
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          aria-live="polite"
          className="font-display text-xl text-ink md:text-2xl"
        >
          {monthLabel(shown.year, shown.month)}
        </h2>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            aria-label={t(lang, "previousMonth")}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-hairline text-xl leading-none text-ink transition-colors hover:bg-card"
            onClick={() => setShown((v) => prevMonth(v))}
          >
            ‹
          </button>
          <button
            type="button"
            aria-label={t(lang, "nextMonth")}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-hairline text-xl leading-none text-ink transition-colors hover:bg-card"
            onClick={() => setShown((v) => nextMonth(v))}
          >
            ›
          </button>
          <button
            type="button"
            className="min-h-11 rounded-full border border-hairline px-4 text-sm font-semibold text-ink transition-colors hover:bg-card"
            onClick={() => setShown(todayYearMonth())}
          >
            {t(lang, "today")}
          </button>
        </div>
      </div>

      <p className="mt-3 text-xs text-ink-soft sm:hidden">
        {t(lang, "calendarScrollHint")}
      </p>
      <div className="mt-3 overflow-x-auto sm:mt-6">
        <table className="w-full min-w-[560px] table-fixed border-collapse">
          <caption className="sr-only">
            {monthLabel(shown.year, shown.month)}
          </caption>
          <thead>
            <tr>
              {WEEKDAY_HEADERS.map((label) => (
                <th
                  key={label}
                  scope="col"
                  className="eyebrow border-b border-hairline pb-2 text-center font-normal"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={`${week[0]?.year}-${week[0]?.month}-${week[0]?.day}`}>
                {week.map((cell) => {
                  const events = cell.inMonth
                    ? (eventsByDay.get(cell.day) ?? [])
                    : [];
                  return (
                    <td
                      key={`${cell.year}-${cell.month}-${cell.day}`}
                      className={`h-24 min-w-20 border border-hairline align-top p-1 sm:h-28 sm:p-2 ${
                        cell.inMonth ? "bg-card" : "bg-ivory-deep/60"
                      }`}
                    >
                      <span
                        className={`text-xs sm:text-sm ${cell.inMonth ? "text-ink" : "text-ink-soft/50"}`}
                      >
                        {cell.day}
                      </span>
                      {events.length > 0 ? (
                        <ul className="mt-1 flex flex-col gap-1">
                          {events.map((event) => (
                            <li key={event.title + event.href}>
                              <a
                                href={
                                  event.href.startsWith("/")
                                    ? localePath(event.href, lang)
                                    : event.href
                                }
                                className="block truncate rounded bg-teal/15 px-1 py-1 text-xs text-teal-deep hover:bg-teal/25"
                              >
                                {event.timeLabel
                                  ? `${event.title} · ${event.timeLabel}`
                                  : event.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
